import { CHAT_API_URL } from '@src/v3/constants/urls';
import { CreateChatPayload } from '@src/v3/interfaces/app/chat';
import { GetChatRoomSchema } from '@src/v3/schemas/components/chat';
import { httpDelete, httpGet, httpPatch, httpPost } from '@src/v3/utils/http';

/**
 * This method is used to perform API request for retrieving message rooms.
 *
 * @param limit - Number of rooms to fetch
 * @param offset - Pagination offset
 * @param filter - 'unread' | 'all' - filter by read status
 * @param search - Keyword search string
 * @param options - Optional callback to receive cancel function for aborting in-flight requests
 * @returns Promise
 */
const getMessageRooms = async (
  limit: number,
  offset?: number | string,
  filter?: 'unread' | 'all',
  search?: string,
  // eslint-disable-next-line no-unused-vars -- cancel is used by caller to abort request
  options?: { onCancel?: (cancel: () => void) => void }
) => {
  let url = CHAT_API_URL.GET_ROOMS;
  url += `?limit=${limit}`;

  if (offset) {
    url += `&offset=${offset}`;
  }

  if (filter) {
    url += `&filter=${filter}`;
  }

  if (search?.trim()) {
    url += `&search=${encodeURIComponent(search.trim())}`;
  }

  const response = await httpGet(url, {
    callback: options?.onCancel ?? undefined,
  });

  return response;
};

/**
 * This method is used to perform API request for retrieving existing message room.
 *
 * @returns Promise
 */
const getExistingMessageRoom = async (param: GetChatRoomSchema) => {
  const url = CHAT_API_URL.GET_EXISTING_ROOM;

  const response = await httpPost(url, param);

  return response;
};

const getRoomMessages = async (
  roomId: string | number,
  limit: number,
  offset?: number | string
) => {
  let url = CHAT_API_URL.GET_ROOM_MESSAGES;
  url = url.replace('#ID#', String(roomId));
  url += `?limit=${limit}`;

  if (offset) {
    url += `&offset=${offset}`;
  }

  const response = await httpGet(url);

  return response;
};

const createMessage = async (params: CreateChatPayload) => {
  const response = await httpPost(CHAT_API_URL.CREATE_MESSAGE, params);

  return response;
};

const createMessageRoom = async (propertyId: string, renterId?: string) => {
  const API_PARAMS: {
    property_id: string;
    renter_id?: string;
  } = {
    property_id: propertyId,
  };

  if (renterId) {
    API_PARAMS.renter_id = renterId;
  }

  const response = await httpPost(CHAT_API_URL.CREATE_MESSAGE_ROOM, API_PARAMS);

  return response;
};

/**
 * This method is used to perform API request for retrieving unread messages.
 *
 * @returns Promise
 */
const getUserHasUnreadMsgs = async () => {
  const url = CHAT_API_URL.GET_USER_HAS_UNREAD_MSGS;

  const response = await httpGet(url);

  return response;
};

/**
 * This method is used to perform API request update AI Enable key in chat room.
 *
 * @returns Promise
 */
const updateAIEnableKeyInChatRoom = async (
  roomId: string,
  param: {
    ai_enabled: boolean;
  }
) => {
  const url = CHAT_API_URL.UPDATE_ROOM.replace('#ROOM_ID#', roomId);

  const response = await httpPatch(url, param);

  return response;
};

/**
 * Deletes linked properties for a specific room.
 *
 * @param {string} roomId - The ID of the room
 * @param {string} propertyId - The ID of the property to be removed
 * @return {Promise<any>} The response from the delete operation
 */
const deleteLinkedProperties = async (roomId: string, propertyId: string) => {
  const url = CHAT_API_URL.REMOVE_LINKED_PROPERTY.replace(
    '#ROOM_ID#',
    roomId
  ).replace('#PROPERTY_ID#', propertyId);

  const response = await httpDelete(url);

  return response;
};

const getFairHousingProposalExist = async (roomId: string) => {
  const response = await httpPost(
    CHAT_API_URL.GET_FAIR_HOUSING_PROPOSAL_EXIST.replace('#ROOM_ID#', roomId),
    {
      room_id: roomId,
    }
  );

  return response;
};

/**
 * Marks messages as read up to and including the given message ID.
 * Used when the user is actively viewing a chat so the backend knows the frontend has read the message.
 */
const readAllMessages = async (messageId: string) => {
  const response = await httpPost(CHAT_API_URL.READ_ALL_MESSAGES, {
    message_id: messageId,
  });
  return response;
};

export {
  getMessageRooms,
  getRoomMessages,
  createMessage,
  createMessageRoom,
  getUserHasUnreadMsgs,
  getExistingMessageRoom,
  updateAIEnableKeyInChatRoom,
  deleteLinkedProperties,
  getFairHousingProposalExist,
  readAllMessages,
};
